import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { modalVars } from '../../../constants/modal-vars';
import { CountriesService } from '../../../core/services/countries/countries.service';
import { MessageService } from '../../../core/services/messages/messages.service';
import { ModalService } from '../../../core/services/modal/modal.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';

function isStringArray(value: unknown[]): value is string[] {
  for (const item of value) {
    if (typeof item !== 'string') {
      return false;
    }
  }

  return true;
}

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectComponent,
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: SelectComponent,
      multi: true
    }
  ],
  standalone: false
})
export class SelectComponent<T extends { value: string | number; name: string; [key: string]: any }>
  implements OnInit, OnDestroy, ControlValueAccessor
{
  items: Array<T>;
  filteredItems: Array<T>;
  isActive = false;
  isReadyToRenderListItems = false;
  required: boolean;
  _selectedItem: T = null;
  highlightedItem: T = null;
  isHeadless = false;
  selectFieldModalId = 'select-field-picker';
  modalVars = modalVars;
  onChange: any;
  onTouched: any;
  private _isFirstValueChange = true;
  private _controlValue: string;
  private _internalFieldControl: UntypedFormControl;
  private readonly _destruction$: Subject<void> = new Subject<void>();

  get fieldControl(): UntypedFormControl {
    return this._internalFieldControl;
  }

  get selectedKeyProperty(): string {
    return this.selectedItem?.[this.keyProperty] || this.selectedItem?.name;
  }

  get selectedItemIndex(): number {
    return this.filteredItems.indexOf(this.selectedItem);
  }
  get highlightedItemIndex(): number {
    return this.filteredItems?.indexOf(this.highlightedItem ?? this.selectedItem);
  }

  get selectedItem(): T {
    return this._selectedItem;
  }
  set selectedItem(selected: T) {
    this._selectedItem = selected;
  }

  @Input() name: string;
  @Input() label: string;
  @Input() tabIndex = -1;
  @Input() id: string;
  @Input() debugging = false;
  @Input() filtering = true;
  @Input() keyProperty = 'name';
  @Input() valueProperty = 'value';
  @Input() cssModifier = '';
  @Input() cssErrorClass = '';
  @Input() openModalOnMobile = false;
  @Input() conditions: { [key: string]: boolean | (() => boolean) };
  @Input('items') set itemArray(items: string[] | T[]) {
    if (isStringArray(items)) {
      const uniqueItems = new Set(items);
      this.items = [...uniqueItems].map((item) => ({ name: item, value: item }) as T);
    } else {
      this.items = items;
    }

    this.filteredItems = this.items;
    if (this.selectedItem) {
      this.selectedItem = this.items.find((item) => item[this.valueProperty] === this.selectedItem[this.valueProperty]);
      this.fieldControl.patchValue(this.selectedKeyProperty, { onlySelf: true, emitEvent: false });
    }
  }
  @Input('mode') set displayMode(displayMode: 'full' | 'headless') {
    this.isHeadless = displayMode === 'headless';
  }
  @Input('required') set isRequired(required: undefined | null | unknown) {
    this.required = !(required === undefined || required === null);
  }
  @Input() hasSecondaryIcon = false;
  @Input() disabled: boolean;

  @Output() selected: EventEmitter<T>;

  @ViewChild('simpleScrollBar', { static: false }) simpleScrollBar: ElementRef;
  @ViewChild('searchInput', { static: false }) searchInputRef: ElementRef;

  @HostBinding('class.headless') get headless(): boolean {
    return this.isHeadless;
  }

  constructor(
    private readonly _messageService: MessageService,
    private readonly _viewportService: ViewportService,
    private readonly _modalService: ModalService,
    private readonly _resizeService: ResizeService,
    private readonly _countriesService: CountriesService
  ) {
    this.selected = new EventEmitter<T>();

    this._resizeService
      .resize()
      .pipe(takeUntil(this._destruction$))
      .subscribe(() => {
        this.toggleActive(false);
      });
  }

  get isMobile() {
    return this._viewportService.isMobileDevice();
  }

  writeValue(value: string): void {
    if (this.selectedItem && this.selectedItem[this.valueProperty] === value) {
      return;
    }

    const filteredItem = this.items.find(
      (item) => (item[this.keyProperty] || item.name) === value || item[this.valueProperty] === value
    );

    if (!filteredItem) {
      return;
    }

    this.selectedItem = filteredItem;
    this.fieldControl.patchValue(this.selectedKeyProperty);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.filteredItems = this.items;
    this._internalFieldControl = new UntypedFormControl();

    if (this.filtering) {
      this.fieldControl.valueChanges
        .pipe(debounceTime(200), takeUntil(this._destruction$))
        .subscribe((value: string) => {
          if (!value) {
            this.filteredItems = this.items;
            this._isFirstValueChange = true;
            this.scrollToListItem('.list__item--selected');
            return;
          }

          let newSelectedItem = this.filteredItems.find(
            (item) =>
              item[this.valueProperty] === value ||
              (item[this.keyProperty] || item.name).toLowerCase() === value.toLowerCase()
          );

          if (!newSelectedItem) {
            const countryCode = this._countriesService.getCountryByLocalizedName(value);
            newSelectedItem = this.items.find((item) => item[this.valueProperty] === countryCode);
            this._isFirstValueChange = true;
          }

          if (newSelectedItem && this._isFirstValueChange) {
            this.onUpdateSearch(newSelectedItem[this.keyProperty], { open: false });
            this.selectedItem = newSelectedItem;
            this.onChange(newSelectedItem[this.valueProperty]);
            this.fieldControl.patchValue(newSelectedItem[this.keyProperty] ?? newSelectedItem.name, {
              onlySelf: true,
              emitEvent: false
            });
            this.scrollToListItem('.list__item--selected');
            return;
          }

          if (this.selectedKeyProperty === value) {
            return;
          }

          this.onUpdateSearch(value);
          this._isFirstValueChange = false;
        });
    }

    setTimeout(() => {
      this.isReadyToRenderListItems = true;
    });
  }

  ngOnDestroy(): void {
    this._destruction$.next();
    this._destruction$.complete();
  }

  validate({ value }: UntypedFormControl) {
    this._controlValue = value;
    const isNotValid = this.selectedItem?.[this.valueProperty] !== value;

    if (!isNotValid) {
      this.fieldControl.setErrors(null);
      return null;
    }

    this.fieldControl.setErrors({ invalid: true });
    return (
      isNotValid && {
        invalid: true
      }
    );
  }

  onClick(event: Event) {
    event.preventDefault();
    if (!this.isActive) {
      this.toggleActive();
    }
  }

  toggleActive(active: boolean = true) {
    if (this.disabled) {
      return;
    }

    this.isActive = active;
    this.highlightedItem = null;
    this.filteredItems = this.items;
    this.isActive && setTimeout(() => this.searchInputRef?.nativeElement?.focus());

    if (this.isActive && this.selectedItem) {
      this.scrollToListItem('.list__item--selected');
    }

    if (!this.isMobile || !this.openModalOnMobile) {
      return;
    }

    if (active) {
      this._modalService.open(this.selectFieldModalId, true);
    } else {
      this._modalService.close(this.selectFieldModalId, true);
    }

    this._messageService.updateOverlayStatus(active, active);
  }

  onSelectItem(selectedItem: T) {
    if (!this.checkEnabled(selectedItem)) {
      return;
    }

    this.selectedItem = selectedItem;
    this.toggleActive(false);
    this.fieldControl.patchValue(this.selectedKeyProperty, { emitEvent: false });

    this.onChange(this.selectedItem[this.valueProperty]);

    this.selected.emit(this.selectedItem);
  }

  onUpdateSearch(value: string, options: { open: boolean } = { open: true }) {
    const { open } = options;
    if (!this.isActive && open) {
      this.toggleActive();
    }

    this.filteredItems = this.items.filter((item) => item[this.keyProperty]?.match(new RegExp(value, 'ig')));
    this.highlightedItem = null;
  }

  onArrowDown(event: Event) {
    event.preventDefault();
    if (this.highlightedItemIndex >= this.filteredItems.length - 1) {
      return;
    }

    const enabledItems = this.filteredItems.filter((item) => this.checkEnabled(item));
    const currentItemIndex = enabledItems.indexOf(this.highlightedItem || this.selectedItem);

    this.highlightedItem = enabledItems[currentItemIndex + 1];
    this.scrollToListItem();
  }

  onArrowUp(event: Event) {
    event.preventDefault();
    if (this.highlightedItemIndex <= 0) {
      return;
    }

    const enabledItems = this.filteredItems.filter((item) => this.checkEnabled(item));
    const currentItemIndex = enabledItems.indexOf(this.highlightedItem || this.selectedItem);
    this.highlightedItem = enabledItems[currentItemIndex - 1];
    this.scrollToListItem();
  }

  onEnterKey(event: Event) {
    event.preventDefault();
    if (!this.isActive) {
      this.toggleActive();
      return;
    }

    if (this.filteredItems.length === 1) {
      this.onSelectItem(this.filteredItems[0]);
      this.toggleActive(false);
      return;
    }

    if (!this.highlightedItem && !this.selectedItem) {
      return;
    }

    this.onSelectItem(this.highlightedItem);
  }

  onEscKey(event: Event) {
    if (!this.isActive) {
      return;
    }

    event?.preventDefault?.();
    this.toggleActive(false);

    if (!this.selectedItem && !this._controlValue) {
      this.fieldControl.patchValue('', { emitEvent: false });
      this.onChange('');
      return;
    }

    if (this._controlValue === this.selectedItem.value) {
      this.fieldControl.patchValue(this.selectedKeyProperty, { emitEvent: false });
      return;
    }

    this.fieldControl.patchValue(this.selectedKeyProperty, { emitEvent: false });
    this.onChange(this.selectedItem[this.valueProperty]);
  }

  onTabKey(event: Event) {
    if (this.isActive) {
      this.toggleActive(false);
    }
  }

  trackBySelectField(index: number, item: T) {
    return item.value;
  }

  scrollToListItem(activeItemSelector = '.list__item--highlight') {
    if (!this.simpleScrollBar) {
      return;
    }

    setTimeout(() => {
      const selectedListElement = this.simpleScrollBar.nativeElement.querySelector(activeItemSelector);
      const simpleBarWrapper = this.simpleScrollBar.nativeElement.querySelector(
        '.simplebar-content-wrapper'
      ) as HTMLElement;

      if (!selectedListElement) {
        simpleBarWrapper.scrollTop = 0;
        return;
      }

      const { scrollTop, clientHeight: scrollHeight } = simpleBarWrapper;
      const { offsetTop: itemTop, offsetHeight: itemHeight } = selectedListElement;
      const scrollBottom = scrollTop + scrollHeight;
      const itemBottom = itemTop + itemHeight;

      if (itemBottom > scrollBottom) {
        simpleBarWrapper.scrollTop = itemTop - scrollHeight + itemHeight;
        return;
      }

      if (itemTop < scrollTop) {
        simpleBarWrapper.scrollTop = itemTop;
        return;
      }
    });
  }

  closeModal(modalId: string, isIphoneModalFix?: boolean) {
    this._modalService.close(modalId, isIphoneModalFix);
  }

  checkEnabled(item: T) {
    return (item?.isEnabled?.() || this.conditions?.[item.value]) ?? true;
  }

  onClear() {
    this.fieldControl.patchValue('', { emitEvent: false });
    this.filteredItems = this.items;
    this.searchInputRef.nativeElement.focus();
  }
}
